import { isDevMode } from '@angular/core';
import {
  PATCH_ARRAY_LINKS,
  PATCH_CONSTRAINTS,
  PATCH_OBJECT_LINKS,
  PATCH_TYPE_LINKS,
  PatchDisabledScope,
  PatchModelType,
} from '../../types/api/dot-net-api-types/patch-constraints';

// Widened aliases needed for dynamic (runtime) lookups: the as-const satisfies type is
// too narrow for string-keyed access ([prop: string]). Recursive helpers receive
// modelType?: string so they always go through the widened aliases. isPathAllowed uses
// PatchModelType at the boundary but delegates to the same aliases for consistency;
// the guard there is unreachable for valid PatchModelType values.
const _constraints = PATCH_CONSTRAINTS as Record<string, Record<string, PatchDisabledScope>>;
const _typeLinks = PATCH_TYPE_LINKS as Record<string, Record<string, string>>;
const _objectLinks = PATCH_OBJECT_LINKS as Record<string, Record<string, string>>;
const _arrayLinks = PATCH_ARRAY_LINKS as Record<string, Record<string, string>>;

/**
 * Searches all models in PATCH_CONSTRAINTS for a property name and returns its scope.
 * Used as a fallback when the parent model type is unknown or has no constraints.
 * First-match-wins on Object.keys order -- prefer passing an explicit modelType.
 * Logs a dev-mode warning when it fires so unintentional reliance surfaces.
 */
function findScopeAcrossModels(prop: string, warnLabel?: string): PatchDisabledScope | undefined {
  for (const model of Object.keys(_constraints)) {
    const scope = _constraints[model]?.[prop];
    if (scope !== undefined) {
      if (warnLabel !== undefined && isDevMode()) {
        console.warn(
          `[patch-validator] Fell back to global scope lookup for '${prop}' (context: ${warnLabel}). ` +
          `Prefer a type link to avoid first-match-wins ambiguity.`,
        );
      }
      return scope;
    }
  }
  return undefined;
}

/**
 * Recursively checks whether a remaining path segment array is allowed.
 * When modelType is known (from PATCH_TYPE_LINKS or PATCH_ARRAY_LINKS) AND has
 * constraints, lookups are resolved directly against that model. Falls back to
 * the global search when the resolved model has no entries in PATCH_CONSTRAINTS.
 */
function isSubPathAllowed(remaining: string[], modelType?: string): boolean {
  if (remaining.length === 0) return true;
  const prop = remaining[0];

  const scope = (modelType && _constraints[modelType])
    ? _constraints[modelType][prop]
    : findScopeAcrossModels(prop, modelType);

  if (scope === undefined) {
    // No constraint on this prop -- check sub-object or array-element links.
    const objectLinkType = modelType ? _objectLinks[modelType]?.[prop] : undefined;
    if (objectLinkType) return isSubPathAllowed(remaining.slice(1), objectLinkType);

    // Array link: collection is freely patchable (add/remove allowed),
    // but element sub-properties may have constraints.
    const arrayLinkType = modelType ? _arrayLinks[modelType]?.[prop] : undefined;
    if (arrayLinkType) {
      // Known gap: whole-element add/replace ops (remaining.length <= 2,
      // e.g. /attorneys/0) pass unfiltered -- the value blob may contain
      // disabled sub-fields. Pre-existing behaviour; flag only if the server
      // rejects whole-object adds that carry derived-field values.
      if (remaining.length <= 2) return true;
      // /prop/index/subfield... -- recurse into element-type constraints.
      return isSubPathAllowed(remaining.slice(2), arrayLinkType);
    }

    return true;
  }
  if (scope === 'scalar') return false;
  if (scope === 'subtree') return false;
  if (scope === 'collection-membership') {
    if (remaining.length < 3) return false;
    const elementType = modelType ? _typeLinks[modelType]?.[prop] : undefined;
    return isSubPathAllowed(remaining.slice(2), elementType);
  }

  return true;
}

/**
 * Returns true if the given JSON Patch path is allowed for the model type.
 *
 * Scope rules:
 *   scalar               - direct replacement of this property is blocked
 *   subtree              - this property and all sub-paths are blocked
 *   collection-membership - the collection and its index slots are blocked;
 *                           sub-field paths recurse into that field's constraints
 *
 * Sub-path recursion uses PATCH_TYPE_LINKS / PATCH_ARRAY_LINKS to resolve the
 * element model type, falling back to a global name search when the element type
 * has no constraints. PATCH_ARRAY_LINKS covers freely-patchable collections whose
 * element types have [PatchDisabled] sub-properties (e.g. FilingDraft.attorneys).
 */
export function isPathAllowed(modelType: PatchModelType, jsonPatchPath: string): boolean {
  if (!_constraints[modelType]) {
    if (isDevMode()) {
      console.warn(`[patch-validator] Unknown modelType '${modelType}' -- readonly enforcement skipped.`);
    }
    return true;
  }

  const modelConstraints = _constraints[modelType];
  const modelLinks = _typeLinks[modelType];

  const segments = jsonPatchPath.replace(/^\//, '').split('/');
  const first = segments[0];
  const scope = modelConstraints?.[first];

  if (scope === undefined) {
    // No constraint on this prop -- check sub-object or array-element links.
    const objectLinkType = _objectLinks[modelType]?.[first];
    if (objectLinkType) return isSubPathAllowed(segments.slice(1), objectLinkType);

    // Array link: collection is freely patchable (add/remove allowed),
    // but element sub-properties may have constraints.
    const arrayLinkType = _arrayLinks[modelType]?.[first];
    if (arrayLinkType) {
      // Known gap: whole-element add/replace ops (segments.length <= 2,
      // e.g. /attorneys/0) pass unfiltered -- the value blob may contain
      // disabled sub-fields. Pre-existing behaviour; flag only if the server
      // rejects whole-object adds that carry derived-field values.
      if (segments.length <= 2) return true;
      // /prop/index/subfield... -- recurse into element-type constraints.
      return isSubPathAllowed(segments.slice(2), arrayLinkType);
    }

    return true;
  }
  if (scope === 'scalar') return false;
  if (scope === 'subtree') return false;
  if (scope === 'collection-membership') {
    if (segments.length < 3) return false;
    const elementType = modelLinks?.[first];
    return isSubPathAllowed(segments.slice(2), elementType);
  }

  return true;
}
